/**
 * Parse LilyGO / Lolin / KGateway QR payloads for routing claim flows.
 * Mirrors native src/utils/parseGatewayQr.ts
 */

function formatMac(mac) {
  const hex = String(mac || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  if (hex.length !== 12) return String(mac || '').toUpperCase();
  return hex.match(/.{2}/g).join(':');
}

function normalizeEspHardwareType(value) {
  const hw = String(value || '').toLowerCase();
  if (!hw) return undefined;
  if (hw === 'wemos_lolin32' || hw.includes('lolin32') || hw.includes('lolin')) {
    return 'wemos_lolin32';
  }
  if (hw.includes('lilygo') || hw.includes('sim7670')) {
    return 'lilygo_tsim7670g';
  }
  return hw;
}

/**
 * @returns {{ kind: 'lilygo'|'kgateway'|'unknown', macAddress?: string, claimCode?: string, hardwareType?: string, raw: string }}
 */
export function parseGatewayQr(data) {
  const raw = (data || '').trim();

  if (raw.toUpperCase().startsWith('LILYGO|')) {
    const parts = raw.split('|');
    return {
      kind: 'lilygo',
      hardwareType: 'lilygo_tsim7670g',
      macAddress: parts[1] ? formatMac(parts[1]) : undefined,
      claimCode: parts[2]?.trim() || undefined,
      raw,
    };
  }

  if (raw.startsWith('http')) {
    try {
      const url = new URL(raw);
      const path = url.pathname.toLowerCase();
      const hardware = normalizeEspHardwareType(url.searchParams.get('hardware'));
      const type = (url.searchParams.get('type') || '').toLowerCase();
      const mac =
        url.searchParams.get('mac') ||
        url.searchParams.get('mac_address') ||
        '';
      const code =
        url.searchParams.get('code') ||
        url.searchParams.get('claim_code') ||
        '';

      const isLily =
        path.includes('lilygo') ||
        String(url.searchParams.get('hardware') || '').toLowerCase().includes('lilygo') ||
        String(url.searchParams.get('hardware') || '').toLowerCase().includes('lolin') ||
        type === 'lilygo';

      return {
        kind: isLily ? 'lilygo' : mac ? 'kgateway' : 'unknown',
        hardwareType: isLily ? (hardware || 'lilygo_tsim7670g') : undefined,
        macAddress: mac ? formatMac(mac) : undefined,
        claimCode: code || undefined,
        raw,
      };
    } catch {
      // fall through
    }
  }

  if (raw.startsWith('{')) {
    try {
      const json = JSON.parse(raw);
      const hardware = normalizeEspHardwareType(json.hardware || json.hardware_type);
      const type = String(json.type || json.hardware || json.hardware_type || '').toLowerCase();
      const isLily =
        type.includes('lilygo') ||
        type.includes('lolin') ||
        hardware === 'wemos_lolin32' ||
        String(json.model || '').toLowerCase().includes('sim7670') ||
        String(json.model || '').toLowerCase().includes('lolin');
      const mac = json.mac || json.macAddress || json.mac_address || json.MAC;
      const code = json.claim_code || json.claimCode || json.code;
      return {
        kind: isLily ? 'lilygo' : mac ? 'kgateway' : 'unknown',
        hardwareType: isLily
          ? (hardware || 'lilygo_tsim7670g')
          : json.hardware_type || undefined,
        macAddress: mac ? formatMac(String(mac)) : undefined,
        claimCode: code ? String(code) : undefined,
        raw,
      };
    } catch {
      // fall through
    }
  }

  if (
    /^[A-F0-9]{12}$/i.test(raw) ||
    /^([A-F0-9]{2}[:-]){5}[A-F0-9]{2}$/i.test(raw)
  ) {
    return { kind: 'kgateway', macAddress: formatMac(raw), raw };
  }

  const macMatch = raw.match(/([A-F0-9]{2}[:-]){5}[A-F0-9]{2}|[A-F0-9]{12}/i);
  if (macMatch) {
    return { kind: 'kgateway', macAddress: formatMac(macMatch[0]), raw };
  }

  return { kind: 'unknown', raw };
}

export function isLilyGoHardwareType(value) {
  const hw = String(value || '').toLowerCase();
  return (
    hw.includes('lilygo') ||
    hw.includes('lolin') ||
    hw === 'wemos_lolin32'
  );
}

export function isEspGatewayHardwareType(value) {
  if (value && typeof value === 'object') {
    const hwModel = String(value.hardware_model || '').toLowerCase();
    return (
      isLilyGoHardwareType(value.hardware_type)
      || hwModel.includes('lolin')
      || hwModel.includes('sim7670')
      || hwModel.includes('tsim')
    );
  }
  return isLilyGoHardwareType(value);
}

export function ensureChillVerifyName(raw, mac) {
  const serial = String(mac || '')
    .replace(/[^0-9A-Fa-f]/g, '')
    .toUpperCase();
  const tail = serial.slice(-6) || 'gateway';
  let cleaned = String(raw || '').trim();
  if (!cleaned) return `chillverify_${tail}`;
  cleaned = cleaned
    .replace(/^(chillverify[_-]*|lilygo[_-]*|lgateway[_-]*)/i, '')
    .replace(/^[_-\s]+/, '')
    .trim();
  return `chillverify_${cleaned || tail}`;
}
